import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home, MaintenanceReport } from '@home-guard/shared';

function apiErr(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: string }).message);
  return 'Something went wrong. Please try again.';
}

function ReportCard({ report }: { report: MaintenanceReport }) {
  const [downloading, setDownloading] = useState(false);

  async function download() {
    setDownloading(true);
    try {
      const res = await fetch(`/api/v1/reports/${report.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `housewizrd-report-${report.id.slice(0, 8)}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(apiErr(e));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              report.report_type === 'premium' ? 'bg-violet-100 text-violet-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {report.report_type === 'premium' ? 'Premium' : 'Standard'}
            </span>
            {report.r2_key && (
              <span className="text-xs bg-green-100 text-green-800 font-semibold px-2 py-0.5 rounded-full">
                Ready
              </span>
            )}
            {!report.r2_key && (
              <span className="text-xs bg-amber-100 text-amber-800 font-semibold px-2 py-0.5 rounded-full">
                Generating…
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">{report.generated_at.slice(0, 10)}</p>
          <p className="text-xs text-gray-400 mt-0.5">Downloaded {report.download_count}×</p>
        </div>
        <button
          onClick={download}
          disabled={downloading || !report.r2_key}
          className="text-sm font-semibold text-blue-700 bg-blue-50 px-4 py-2 rounded-xl disabled:opacity-40"
        >
          {downloading ? 'Downloading…' : 'Download'}
        </button>
      </div>
    </div>
  );
}

export function MaintenanceReport() {
  const navigate = useNavigate();
  const [purchasing, setPurchasing] = useState<'standard' | 'premium' | null>(null);

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });

  const home = (homesData?.data ?? []).find(h => h.is_primary) ?? homesData?.data?.[0];

  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['reports', home?.id],
    queryFn: () => api.get<{ data: MaintenanceReport[] }>(`/homes/${home!.id}/reports`),
    enabled: !!home?.id,
  });

  const purchaseMutation = useMutation({
    mutationFn: (reportType: 'standard' | 'premium') =>
      api.post<{ checkout_url: string }>(`/homes/${home!.id}/reports/purchase`, {
        report_type: reportType,
        success_url: `${window.location.origin}/reports?success=1`,
        cancel_url: `${window.location.origin}/reports`,
      }),
    onSuccess: (data) => {
      window.location.href = data.checkout_url;
    },
    onError: (e) => {
      alert(apiErr(e));
      setPurchasing(null);
    },
  });

  const reports = reportsData?.data ?? [];

  return (
    <div className="min-h-dvh bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-700 to-blue-900 pt-12 pb-6 px-4">
        <button
          onClick={() => navigate('/app/dashboard')}
          className="flex items-center gap-1 text-white/80 text-sm font-medium mb-4"
          style={{ minHeight: 44 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Dashboard
        </button>
        <h1 className="text-white text-2xl font-bold">Maintenance Record</h1>
        <p className="text-white/70 text-sm mt-1">Prove your home was maintained — perfect for resale</p>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Value prop */}
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
          <p className="font-semibold text-gray-900">Housewizrd Certified Maintenance Record</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            A comprehensive PDF of your home's maintenance history, appliance records, health score trend, and contractor work — ready to share with your realtor or buyer.
          </p>
          <div className="space-y-2 text-sm text-gray-700">
            {[
              'All completed maintenance tasks with dates and costs',
              'Full appliance inventory and service history',
              'Home systems summary (pipes, roof, HVAC, etc.)',
              'Maintenance health score trend over time',
            ].map(item => (
              <div key={item} className="flex items-start gap-2">
                <span className="text-green-600 font-bold mt-0.5">✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-2 gap-3">
          {/* Standard */}
          <div className="bg-white rounded-2xl shadow-sm p-4 flex flex-col gap-3">
            <div>
              <p className="font-bold text-gray-900">Standard</p>
              <p className="text-2xl font-black text-blue-700 mt-1">$19</p>
              <p className="text-xs text-gray-500 mt-1">One-time purchase</p>
            </div>
            <ul className="text-xs text-gray-600 space-y-1 flex-1">
              <li>✓ Complete task history</li>
              <li>✓ Appliance records</li>
              <li>✓ Home systems profile</li>
            </ul>
            <button
              onClick={() => { setPurchasing('standard'); purchaseMutation.mutate('standard'); }}
              disabled={!!purchasing || !home}
              className="w-full py-2.5 bg-blue-700 text-white rounded-xl text-sm font-bold disabled:opacity-50"
            >
              {purchasing === 'standard' ? 'Redirecting…' : 'Buy Report'}
            </button>
          </div>

          {/* Premium */}
          <div className="bg-gradient-to-b from-violet-50 to-white border-2 border-violet-200 rounded-2xl p-4 flex flex-col gap-3">
            <div>
              <p className="font-bold text-gray-900">Premium</p>
              <p className="text-2xl font-black text-violet-700 mt-1">$29</p>
              <p className="text-xs text-gray-500 mt-1">One-time purchase</p>
            </div>
            <ul className="text-xs text-gray-600 space-y-1 flex-1">
              <li>✓ Everything in Standard</li>
              <li>✓ Contractor ratings</li>
              <li>✓ Cost savings analysis</li>
            </ul>
            <button
              onClick={() => { setPurchasing('premium'); purchaseMutation.mutate('premium'); }}
              disabled={!!purchasing || !home}
              className="w-full py-2.5 bg-violet-700 text-white rounded-xl text-sm font-bold disabled:opacity-50"
            >
              {purchasing === 'premium' ? 'Redirecting…' : 'Buy Report'}
            </button>
          </div>
        </div>

        <p className="text-xs text-center text-gray-400">
          Secure payment via Stripe · Instant delivery after payment
        </p>

        {/* Past reports */}
        {isLoading && (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {reports.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Your Reports</p>
            <div className="space-y-3">
              {reports.map(r => <ReportCard key={r.id} report={r} />)}
            </div>
          </div>
        )}

        <div className="text-center">
          <Link to="/app/vault" className="text-sm text-blue-700 font-medium">
            View Document Vault →
          </Link>
        </div>
      </div>

      <NavBar />
    </div>
  );
}
